import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Star, StarBorder } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link } from "react-router";
import {
  CommodityDots,
  InfoRow,
  formatTickers,
} from "./functions";
import {
  COMPANY_LIFECYCLE_OPTIONS,
  COMPANY_LIFECYCLE_STAGE_OPTIONS,
  COMPANY_TYPE_OPTIONS,
} from "../../constants/companyFields";

const choiceLabel = (value, options) => {
  if (value === null || value === undefined || value === "") return "—";
  const lower = `${value}`.toLowerCase();
  const match = options.find((opt) => opt.value.toLowerCase() === lower);
  return match ? match.label : value;
};

export default function CompanyQuickViewDrawer({
  open,
  onClose,
  company,
  isLoggedIn = false,
  favorite = false,
  onToggleFavorite,
  viewProfileHref,
  viewProfileTarget = "_blank",
}) {
  const profileHref = viewProfileHref || (company?.slug ? `/companies/${company.slug}` : "");
  const showFavorite = Boolean(onToggleFavorite);
  const openInNewTab = viewProfileTarget === "_blank";
  const buttonProps = openInNewTab
    ? {
        component: "a",
        href: profileHref || "#",
        target: viewProfileTarget,
        rel: "noopener noreferrer",
      }
    : {
        component: Link,
        to: profileHref || "#",
      };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 420 } } } }}
      ModalProps={{
        BackdropProps: {
          sx: { backgroundColor: "rgba(0,0,0,0.25)" },
        },
      }}
    >
      {company && (
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {company.name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {company.is_claimed && (
                <Tooltip title="Claimed" placement="top">
                  <CheckCircleIcon color="primary" fontSize="small" />
                </Tooltip>
              )}
              {showFavorite && (
                <>
                  {isLoggedIn ? (
                    <IconButton
                      onClick={(event) => {
                        event.preventDefault();
                        onToggleFavorite(company);
                      }}
                      size="small"
                      sx={{
                        color: favorite ? "warning.main" : "text.secondary",
                      }}
                    >
                      {favorite ? <Star /> : <StarBorder />}
                    </IconButton>
                  ) : (
                    <Tooltip title="Log in to favorite companies">
                      <Box component="span" sx={{ lineHeight: 0 }}>
                        <IconButton disabled size="small" sx={{ pointerEvents: "none" }}>
                          <StarBorder />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {company?.profile?.hq_city ? `${company.profile.hq_city}, ` : ""}
            {company?.profile?.hq_country}
          </Typography>

          <Box sx={{ mt: 2 }}>
            <CommodityDots commodities={company?.profile?.commodities} />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1.25} flex={1} sx={{ overflowY: "auto", pr: 0.5 }}>
            <InfoRow
              label="Company Type"
              value={choiceLabel(company?.profile?.company_type, COMPANY_TYPE_OPTIONS)}
            />
            <InfoRow
              label="Lifecycle"
              value={choiceLabel(company?.profile?.company_lifecycle, COMPANY_LIFECYCLE_OPTIONS)}
            />
            <InfoRow
              label="Lifecycle Stage"
              value={choiceLabel(
                company?.profile?.company_lifecycle_stage,
                COMPANY_LIFECYCLE_STAGE_OPTIONS
              )}
            />
            <InfoRow label="Website" value={company?.profile?.website_url} />
            <InfoRow label="Contact Email" value={company?.profile?.contact_email} />
            <InfoRow label="Contact Phone" value={company?.profile?.contact_phone} />
            <InfoRow
              label="Tickers"
              value={formatTickers(company?.profile?.tickers_exchanges)}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Description
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: "pre-line" }}>
                {company?.profile?.corporate_description || "No description available yet."}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Button
            fullWidth
            variant="contained"
            disabled={!profileHref}
            onClick={onClose}
            {...buttonProps}
          >
            View Full Profile
          </Button>
        </Box>
      )}
    </Drawer>
  );
}
